import DOMPurify from 'dompurify';

/**
 * Sanitize HTML content to prevent XSS attacks
 * @param {string} dirty - The potentially unsafe HTML string
 * @returns {string} - Sanitized HTML string
 */
export const sanitizeHtml = (dirty) => {
  if (!dirty) return '';
  return DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'br', 'p', 'span'],
    ALLOWED_ATTR: [],
  });
};

/**
 * Sanitize text content (strips all HTML)
 * @param {string} dirty - The potentially unsafe string
 * @returns {string} - Plain text without any HTML
 */
export const sanitizeText = (dirty) => {
  if (!dirty) return '';
  return DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
  });
};

/**
 * Escape special characters for safe display
 * @param {string} str - String to escape
 * @returns {string} - Escaped string
 */
export const escapeHtml = (str) => {
  if (!str) return '';
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
};

/**
 * Sanitize error messages to prevent sensitive data leakage
 * @param {Error|string} error - The error object or message
 * @returns {string} - Safe error message for display
 */
export const sanitizeErrorMessage = (error) => {
  const sensitivePatterns = [
    /password/i,
    /secret/i,
    /token/i,
    /api[_-]?key/i,
    /auth/i,
    /credential/i,
    /database/i,
    /sql/i,
    /connection/i,
    /internal server/i,
    /stack trace/i,
    /at\s+\w+\s+\(/i, // Stack trace pattern
  ];

  let message = '';

  if (typeof error === 'string') {
    message = error;
  } else if (error?.response?.data?.detail) {
    message = error.response.data.detail;
  } else if (error?.message) {
    message = error.message;
  } else {
    return 'An unexpected error occurred';
  }

  // Check if message contains sensitive information
  for (const pattern of sensitivePatterns) {
    if (pattern.test(message)) {
      return 'An error occurred. Please try again or contact support.';
    }
  }

  // Truncate long messages
  if (message.length > 200) {
    return message.substring(0, 200) + '...';
  }

  return sanitizeText(message);
};

export default {
  sanitizeHtml,
  sanitizeText,
  escapeHtml,
  sanitizeErrorMessage,
};
